import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  getStorefrontCustomProductCollectionForAdmin,
  listStorefrontCustomProductCollectionPickerOptions,
  upsertStorefrontCustomProductCollectionForAdmin,
  type StorefrontCustomProductCollectionAdminDetail,
  type UpsertStorefrontCustomProductCollectionInput,
} from '../services/storefront-custom-product-collections-admin.service';
import type { SupabaseClient } from '../supabase/client';
import type { StorefrontPublicCustomProductCollectionPickerOption } from '../types/storefront-engine';

export const storefrontCustomProductCollectionAdminKeys = {
  all: ['storefront-custom-product-collections-admin'] as const,
  lists: (storeId: string) =>
    ['storefront-custom-product-collections-admin', 'list', storeId] as const,
  list: (storeId: string, search: string) =>
    ['storefront-custom-product-collections-admin', 'list', storeId, search] as const,
  detail: (storeId: string, collectionRef: string) =>
    ['storefront-custom-product-collections-admin', 'detail', storeId, collectionRef] as const,
};

export function useStorefrontCustomProductCollectionPickerOptions(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly search: string;
  },
): UseQueryResult<StorefrontPublicCustomProductCollectionPickerOption[]> {
  const storeId = args.storeId?.trim() ?? '';
  const search = args.search.trim();
  return useQuery({
    queryKey: storeId
      ? storefrontCustomProductCollectionAdminKeys.list(storeId, search)
      : storefrontCustomProductCollectionAdminKeys.list('pending', search),
    enabled: storeId !== '',
    queryFn: () =>
      listStorefrontCustomProductCollectionPickerOptions(client, {
        storeId,
        search,
      }),
    staleTime: 30_000,
  });
}

export function useStorefrontCustomProductCollection(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly collectionRef: string | null;
    readonly supabaseUrl: string;
  },
): UseQueryResult<StorefrontCustomProductCollectionAdminDetail | null> {
  const storeId = args.storeId?.trim() ?? '';
  const collectionRef = args.collectionRef?.trim() ?? '';
  return useQuery({
    queryKey:
      storeId && collectionRef
        ? storefrontCustomProductCollectionAdminKeys.detail(storeId, collectionRef)
        : storefrontCustomProductCollectionAdminKeys.detail('pending', collectionRef),
    enabled: storeId !== '' && collectionRef !== '',
    queryFn: () =>
      getStorefrontCustomProductCollectionForAdmin(
        client,
        { storeId, collectionRef },
        { supabaseUrl: args.supabaseUrl },
      ),
    staleTime: 30_000,
  });
}

export function useUpsertStorefrontCustomProductCollection(
  client: SupabaseClient,
  options: { readonly supabaseUrl: string },
): UseMutationResult<
  StorefrontCustomProductCollectionAdminDetail,
  unknown,
  UpsertStorefrontCustomProductCollectionInput
> {
  return useMutation({
    mutationFn: (input) =>
      upsertStorefrontCustomProductCollectionForAdmin(client, input, {
        supabaseUrl: options.supabaseUrl,
      }),
  });
}
